import React, { useContext, useMemo } from 'react';
import _ from 'lodash';
import styled from 'styled-components';

import Tooltip from 'components/Tooltip';
import Row from 'components/Common/Row';
import UsersContext from 'contexts/UsersContext';
import isoDateToAmerican from 'isoDateToAmerican';
import userTypeCheckers from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/UsersProvider/userTypeCheckers';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import toSentenceCase from 'services/toSentenceCase';
import useValueFormatters from 'hooks/useValueFormatters';
import usePaymentCreatorName from 'components/DriverAX/components/AdditionalPayments/usePaymentCreatorName';
import { getPaymentDisplay } from 'components/DriverAX/components/AdditionalPayments/utils';

export type BonusOverrideMetaData = (
  | {
      updatedBy: string;
      comments: string;
      // Reason codes recorded once for the whole override change. Optional
      // because it depends on the backend projecting them onto `_overrides`
      // (see ENG-860) — rendered defensively when present.
      reasonCodes?: string[];
      label: string;
      updatedOn: string;
      type: 'qualifier';
    }
  | {
      reward: string;
      updatedBy: string;
      comments: string;
      reasonCodes?: string[];
      field: string;
      label: string;
      updatedOn: string;
      type: 'criteria';
    }
  | {
      // Backend `_overrides` reward entry (asset-utilization !134): field === reward === rewardName,
      // label = "{rewardName} reward overridden".
      reward: string;
      field: string;
      updatedBy: string;
      comments: string;
      reasonCodes?: string[];
      label: string;
      updatedOn: string;
      type: 'reward';
    }
) & {
  // The sub-period (week) the entry belongs to. Set when entries from several
  // weekly records are merged into one row (aggregated sign-off boards); the
  // tooltip then groups them under a heading per week.
  periodLabel?: string;
};

export type BonusExclusionMetaData = {
  field: string;
  reward: string;
  label: string;
  comments: string;
  updatedBy: string;
  updatedOn: string;
  periodLabel?: string;
};

// Backend `_additionalPayments` entry (ENG-1263): an ad-hoc recognition
// payment already included in the record's pay.
export type BonusAdditionalPaymentMetaData =
  BonusTypes.AdditionalPayments.Payment & {
    periodLabel?: string;
  };

// Groups entries by `periodLabel` (in first-seen order) when any entry carries
// one; otherwise a single unlabelled group, so single-record rows render as
// before.
const groupByPeriod = <T extends { periodLabel?: string }>(
  entries: T[],
): { periodLabel: string | undefined; entries: T[] }[] => {
  if (!entries.some((e) => e.periodLabel !== undefined)) {
    return [{ periodLabel: undefined, entries }];
  }
  const labels = _.uniq(entries.map((e) => e.periodLabel));
  return labels.map((periodLabel) => ({
    periodLabel,
    entries: entries.filter((e) => e.periodLabel === periodLabel),
  }));
};

const PeriodHeading = ({ periodLabel }: { periodLabel: string | undefined }) =>
  periodLabel ? (
    <Typography.Header type={'H5'} color={'var(--color-tooltip-text)'}>
      {periodLabel}
    </Typography.Header>
  ) : null;

const BonusOverrideTooltip = ({
  bonusOverridesMetaData,
}: {
  bonusOverridesMetaData: BonusOverrideMetaData[];
}) => {
  // Per week (when labelled), then per change (updatedOn) within it.
  const groups = useMemo(
    () =>
      groupByPeriod(bonusOverridesMetaData).map(({ periodLabel, entries }) => ({
        periodLabel,
        sections: _.uniqBy(entries, 'updatedOn').map(({ updatedOn }) => ({
          updatedOn,
          overrides: entries.filter((o) => o.updatedOn === updatedOn),
        })),
      })),
    [bonusOverridesMetaData],
  );

  return (
    <div
      style={{
        gap: '8px',
        display: 'flex',
        flexDirection: 'column',
        padding: '12px 4px',
      }}
    >
      <div>
        <Typography.Header type={'H3'} color={'var(--color-tooltip-text)'}>
          {`Override${bonusOverridesMetaData.length === 1 ? '' : 's'} on:`}
        </Typography.Header>
      </div>

      {groups.map(({ periodLabel, sections }) => (
        <React.Fragment key={periodLabel ?? 'all'}>
          <PeriodHeading periodLabel={periodLabel} />
          {sections.map(({ overrides, updatedOn }) => (
            <BonusOverrideTooltipSection
              key={updatedOn}
              bonusOverridesMetaData={overrides}
            />
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};

const BonusOverrideTooltipSection = ({
  bonusOverridesMetaData,
}: {
  bonusOverridesMetaData: BonusOverrideMetaData[];
}) => {
  const { allUsers } = useContext(UsersContext);
  const updatedBy = useMemo((): string | undefined => {
    if (bonusOverridesMetaData.length === 0) {
      return undefined;
    }

    const user = allUsers.find(
      (u) => u.id === bonusOverridesMetaData[0].updatedBy,
    );

    if (!user || userTypeCheckers.isWallboardUser(user)) {
      return undefined;
    }

    return user.displayName;
  }, [allUsers, bonusOverridesMetaData]);

  const updatedOn = useMemo(() => {
    if (bonusOverridesMetaData.length === 0) {
      return '';
    }

    return isoDateToAmerican(bonusOverridesMetaData[0].updatedOn);
  }, [bonusOverridesMetaData]);

  if (bonusOverridesMetaData.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        backgroundColor: 'var(--color-tooltip-section-bg)',
        padding: 8,
      }}
    >
      <div
        style={{
          borderBottom: '1px solid var(--color-border-secondary)',
          marginBottom: '8px',
          paddingBottom: '8px',
        }}
      >
        {bonusOverridesMetaData.map((override, index) => (
          // Stable per render: a random key remounted every entry on each render.
          <div key={`${override.type}-${index}`} style={{ marginBottom: 8 }}>
            <Typography.Header type={'H6'} color={'var(--color-tooltip-text)'}>
              {override.type === 'reward'
                ? // The backend label ("{reward} reward overridden") is the full statement, so
                  // show it as the single header — avoids a redundant reward-name callout above it.
                  toSentenceCase(override.label, true)
                : override.type === 'criteria'
                  ? toSentenceCase(override.reward)
                  : 'Qualifier'}
            </Typography.Header>
            {override.type !== 'reward' && (
              <Typography.Body
                type={'Body 13 Bold'}
                color={'var(--color-tooltip-text)'}
              >
                {override.label}
              </Typography.Body>
            )}
          </div>
        ))}
      </div>
      <div>
        {!!bonusOverridesMetaData[0].comments &&
          bonusOverridesMetaData[0].comments !== '' && (
            <div>
              <Typography.Body
                type={'Body 13'}
                color={'var(--color-tooltip-text)'}
              >
                {`Note: '${bonusOverridesMetaData[0].comments}'`}
              </Typography.Body>
            </div>
          )}
        {!!bonusOverridesMetaData[0].reasonCodes &&
          bonusOverridesMetaData[0].reasonCodes.length > 0 && (
            <div>
              <Typography.Body
                type={'Body 13'}
                color={'var(--color-tooltip-text)'}
              >
                {`Reason: ${bonusOverridesMetaData[0].reasonCodes.join(', ')}`}
              </Typography.Body>
            </div>
          )}
        <Row centerAlign style={{ gap: 8 }}>
          <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
            {updatedBy}
          </Typography.Body>
          <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
            {updatedOn}
          </Typography.Body>
        </Row>
      </div>
    </div>
  );
};

const BonusExclusionTooltip = ({
  bonusExclusionsMetaData,
}: {
  bonusExclusionsMetaData: BonusExclusionMetaData[];
}) => {
  const groups = useMemo(
    () =>
      groupByPeriod(bonusExclusionsMetaData).map(
        ({ periodLabel, entries }) => ({
          periodLabel,
          sections: _.uniqBy(entries, 'updatedOn').map(({ updatedOn }) => ({
            updatedOn,
            exclusions: entries.filter((e) => e.updatedOn === updatedOn),
          })),
        }),
      ),
    [bonusExclusionsMetaData],
  );

  return (
    <div
      style={{
        gap: '8px',
        display: 'flex',
        flexDirection: 'column',
        padding: '12px 4px',
      }}
    >
      <div>
        <Typography.Header type={'H3'} color={'var(--color-tooltip-text)'}>
          {`Exclusion${bonusExclusionsMetaData.length === 1 ? '' : 's'} on:`}
        </Typography.Header>
      </div>
      {groups.map(({ periodLabel, sections }) => (
        <React.Fragment key={periodLabel ?? 'all'}>
          <PeriodHeading periodLabel={periodLabel} />
          {sections.map(({ exclusions, updatedOn }) => (
            <BonusExclusionTooltipSection
              key={updatedOn}
              bonusExclusionsMetaData={exclusions}
            />
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};

const BonusExclusionTooltipSection = ({
  bonusExclusionsMetaData,
}: {
  bonusExclusionsMetaData: BonusExclusionMetaData[];
}) => {
  const { allUsers } = useContext(UsersContext);

  const updatedBy = useMemo((): string | undefined => {
    if (bonusExclusionsMetaData.length === 0) {
      return undefined;
    }
    const user = allUsers.find(
      (u) => u.id === bonusExclusionsMetaData[0].updatedBy,
    );
    if (!user || userTypeCheckers.isWallboardUser(user)) {
      return undefined;
    }
    return user.displayName;
  }, [allUsers, bonusExclusionsMetaData]);

  const updatedOn = useMemo(() => {
    if (bonusExclusionsMetaData.length === 0) {
      return '';
    }
    return isoDateToAmerican(bonusExclusionsMetaData[0].updatedOn);
  }, [bonusExclusionsMetaData]);

  if (bonusExclusionsMetaData.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        backgroundColor: 'var(--color-tooltip-section-bg)',
        padding: 8,
      }}
    >
      <div
        style={{
          borderBottom: '1px solid var(--color-border-secondary)',
          marginBottom: '8px',
          paddingBottom: '8px',
        }}
      >
        {bonusExclusionsMetaData.map((exclusion) => (
          <div key={exclusion.field} style={{ marginBottom: 8 }}>
            <Typography.Header type={'H6'} color={'var(--color-tooltip-text)'}>
              {toSentenceCase(exclusion.reward)}
            </Typography.Header>
            <Typography.Body
              type={'Body 13 Bold'}
              color={'var(--color-tooltip-text)'}
            >
              {exclusion.label}
            </Typography.Body>
          </div>
        ))}
      </div>
      <div>
        {bonusExclusionsMetaData[0].comments &&
          bonusExclusionsMetaData[0].comments !== '' && (
            <Typography.Body
              type={'Body 13'}
              color={'var(--color-tooltip-text)'}
            >
              {`'${bonusExclusionsMetaData[0].comments}'`}
            </Typography.Body>
          )}
        <Row centerAlign style={{ gap: 8 }}>
          <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
            {updatedBy}
          </Typography.Body>
          <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
            {updatedOn}
          </Typography.Body>
        </Row>
      </div>
    </div>
  );
};

const PaymentAmountPill = styled.div`
  background-color: var(--color-tooltip-positive-bg);
  border-radius: 999px;
  padding: 2px 8px;
`;

const BonusAdditionalPaymentTooltipSection = ({
  payment,
}: {
  payment: BonusAdditionalPaymentMetaData;
}) => {
  const { formatValue } = useValueFormatters();
  const createdBy = usePaymentCreatorName(payment);
  const { title, detail } = getPaymentDisplay(payment);

  return (
    <div
      style={{
        backgroundColor: 'var(--color-tooltip-section-bg)',
        borderRadius: 6,
        padding: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <Row spaceBetween centerAlign style={{ gap: 16 }}>
        <PaymentAmountPill>
          <Typography.Body
            type={'Body 13 Bold'}
            color={'var(--color-tooltip-positive)'}
          >
            {`+${formatValue({
              value: payment.amount,
              formatting: { prefix: 'currency', precision: 2 },
            })}`}
          </Typography.Body>
        </PaymentAmountPill>
        <Typography.Body
          type={'Body 12'}
          color={'var(--color-tooltip-text-muted)'}
        >
          {isoDateToAmerican(payment.createdOn)}
        </Typography.Body>
      </Row>
      <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
        {title}
      </Typography.Body>
      {detail && (
        <Typography.Body type={'Body 12'} color={'var(--color-tooltip-text)'}>
          {detail}
        </Typography.Body>
      )}
      {createdBy && (
        <Typography.Body
          type={'Body 12'}
          color={'var(--color-tooltip-text-muted)'}
        >
          {`by ${createdBy}`}
        </Typography.Body>
      )}
    </div>
  );
};

const BonusAdditionalPaymentsTooltip = ({
  bonusAdditionalPaymentsMetaData,
  isBelowOtherSections,
}: {
  bonusAdditionalPaymentsMetaData: BonusAdditionalPaymentMetaData[];
  // Separate it from the override / exclusion sections above, so the
  // payments don't read as part of an override.
  isBelowOtherSections: boolean;
}) => {
  const { formatValue } = useValueFormatters();
  const groups = useMemo(
    () => groupByPeriod(bonusAdditionalPaymentsMetaData),
    [bonusAdditionalPaymentsMetaData],
  );
  const total = useMemo(
    () =>
      bonusAdditionalPaymentsMetaData.reduce(
        (sum, payment) => sum + Math.round(payment.amount * 100),
        0,
      ) / 100,
    [bonusAdditionalPaymentsMetaData],
  );

  return (
    <div
      style={{
        gap: '8px',
        display: 'flex',
        flexDirection: 'column',
        padding: '12px 4px',
        ...(isBelowOtherSections
          ? { borderTop: '1px solid var(--color-tooltip-divider)' }
          : {}),
      }}
    >
      <Row spaceBetween centerAlign style={{ gap: 16 }}>
        <Typography.Header type={'H3'} color={'var(--color-tooltip-text)'}>
          Additional Payments
        </Typography.Header>
        <Typography.Body
          type={'Body 14 Bold'}
          color={'var(--color-tooltip-positive)'}
        >
          {`+${formatValue({
            value: total,
            formatting: { prefix: 'currency', precision: 2 },
          })}`}
        </Typography.Body>
      </Row>
      {groups.map(({ periodLabel, entries }) => (
        <React.Fragment key={periodLabel ?? 'all'}>
          <PeriodHeading periodLabel={periodLabel} />
          {entries.map((payment) => (
            <BonusAdditionalPaymentTooltipSection
              key={payment.id}
              payment={payment}
            />
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Icon whose tooltip lists a record's bonus overrides / exclusions
 * / recognition payments (backend `_overrides` / `_exclusions` /
 * `_additionalPayments` enrichment). Used on the board driver
 * column and on overridden cells of the slideout's weekly fact table.
 */
const BonusOverrideIndicator = ({
  driver,
  bonusOverridesMetaData,
  bonusExclusionsMetaData,
  bonusAdditionalPaymentsMetaData,
  testId,
}: {
  driver: string;
  bonusOverridesMetaData: BonusOverrideMetaData[];
  bonusExclusionsMetaData?: BonusExclusionMetaData[];
  bonusAdditionalPaymentsMetaData?: BonusAdditionalPaymentMetaData[];
  testId?: string;
}) => {
  const hasOverridesOrExclusions =
    bonusOverridesMetaData.length > 0 ||
    (bonusExclusionsMetaData?.length ?? 0) > 0;

  return (
    <div style={{ minWidth: 12, flexShrink: 0, display: 'flex' }}>
      <Tooltip
        content={
          <div>
            {bonusOverridesMetaData.length > 0 && (
              <BonusOverrideTooltip
                bonusOverridesMetaData={bonusOverridesMetaData}
              />
            )}
            {bonusExclusionsMetaData && bonusExclusionsMetaData.length > 0 && (
              <BonusExclusionTooltip
                bonusExclusionsMetaData={bonusExclusionsMetaData}
              />
            )}
            {bonusAdditionalPaymentsMetaData &&
              bonusAdditionalPaymentsMetaData.length > 0 && (
                <BonusAdditionalPaymentsTooltip
                  bonusAdditionalPaymentsMetaData={
                    bonusAdditionalPaymentsMetaData
                  }
                  isBelowOtherSections={hasOverridesOrExclusions}
                />
              )}
          </div>
        }
      >
        <Icon
          icon={'info'}
          color={'var(--color-status-warning)'}
          width={12}
          height={12}
          testId={testId ?? `${driver}-override-indicator`}
        />
      </Tooltip>
    </div>
  );
};

export default BonusOverrideIndicator;
